import { useCurrentUser } from './useCurrentUser';
import type { UserRole } from '@globalbridge/types';
import { isCatalogBrandEnabled } from '../lib/featureFlags';
import { brandCatalogOpen } from '../lib/brandCatalogAccess';

export function usePermissions() {
  const { user } = useCurrentUser();

  const hasRole = (role: UserRole): boolean => {
    return user?.role === role;
  };

  const hasAnyRole = (roles: UserRole[]): boolean => {
    return user ? roles.includes(user.role) : false;
  };

  const isSuperAdmin = (): boolean => {
    return user?.role === 'SUPER_ADMIN';
  };

  const isAdmin = (): boolean => {
    return user?.role === 'SUPER_ADMIN' || user?.role === 'ADMIN';
  };

  const isAdminOrHigher = (): boolean => {
    return user?.role === 'SUPER_ADMIN' || user?.role === 'ADMIN';
  };

  const isBrandAdmin = (): boolean => {
    return user?.role === 'BRAND_ADMIN';
  };

  const isBrandManager = (): boolean => {
    return user?.role === 'BRAND_MANAGER';
  };

  const isFinance = (): boolean => {
    return user?.role === 'FINANCE';
  };

  const canEditUsers = (): boolean => {
    return user?.role === 'SUPER_ADMIN';
  };

  const canEditOwnProfile = (): boolean => {
    // All users can edit their own profile except finance (read-only)
    return !isFinance();
  };

  const canManageBrands = (): boolean => {
    return hasAnyRole(['SUPER_ADMIN', 'ADMIN', 'BRAND_ADMIN']);
  };

  const canViewAnalytics = (): boolean => {
    return hasAnyRole(['SUPER_ADMIN', 'ADMIN', 'BRAND_ADMIN', 'BRAND_MANAGER']);
  };

  const canManageTeam = (): boolean => {
    return user?.role === 'BRAND_ADMIN';
  };

  const canAccessPortal = (): boolean => {
    return hasAnyRole(['SUPER_ADMIN', 'ADMIN', 'BRAND_ADMIN', 'BRAND_MANAGER']);
  };

  const canAccessFinance = (): boolean => {
    return hasAnyRole(['SUPER_ADMIN', 'ADMIN', 'BRAND_ADMIN', 'FINANCE']);
  };

  /**
   * Catalog (AI onboarding). Brand admins own their catalog; GB admins run it
   * from the admin portal on behalf of a brand (Ari, 2026-08-25) — every
   * admin catalog call then carries an explicit brandId via CatalogRoute.
   */
  const canAccessCatalog = (): boolean => {
    return hasAnyRole(['SUPER_ADMIN', 'ADMIN', 'BRAND_ADMIN']);
  };

  /**
   * Catalog goes live for GB admins only (Ari, 2026-09-10): one operator
   * onboards every brand's products from the main shell, choosing the brand in
   * the navbar switcher. Brand admins are held back until the brand rollout —
   * flip the sidebar + CatalogRoute gates back to `canAccessCatalog` then.
   */
  const canOperateCatalog = (): boolean => {
    return hasAnyRole(['SUPER_ADMIN', 'ADMIN']);
  };

  /**
   * The brand catalog for a brand user (Ari, 2026-10-20): the
   * VITE_CATALOG_BRAND_ENABLED kill switch on, a brand admin, and the server
   * says this brand has catalog access (onboarding record or the admin switch).
   */
  const canUseBrandCatalog = (): boolean => brandCatalogOpen(isCatalogBrandEnabled(), user);

  /**
   * File and view shipment notices (/shipments, /shipments/new). Mirrors the
   * API's ACTING_ROLES (apps/api/src/common/brand-access.service.ts), which
   * gates productsPage.shipAsn: FINANCE may not file.
   */
  const canShipToWarehouse = (): boolean => {
    return hasAnyRole(['SUPER_ADMIN', 'ADMIN', 'BRAND_ADMIN', 'BRAND_MANAGER']);
  };

  const canViewUserActivity = (): boolean => {
    return user?.role === 'SUPER_ADMIN';
  };

  return {
    user,
    hasRole,
    hasAnyRole,
    isSuperAdmin,
    isAdmin,
    isAdminOrHigher,
    isBrandAdmin,
    isBrandManager,
    isFinance,
    canEditUsers,
    canEditOwnProfile,
    canManageBrands,
    canViewAnalytics,
    canManageTeam,
    canAccessPortal,
    canAccessFinance,
    canAccessCatalog,
    canOperateCatalog,
    canUseBrandCatalog,
    canShipToWarehouse,
    canViewUserActivity,
  };
}
